import axiosInstance from '../auth/axiosInstance';
import { assertApiSuccess, getApiErrorMessage, type ApiEnvelope } from '../types/api';
import type {
  AssessmentFormSchema,
  DynamicAssessmentResponse,
  GetAssessmentFormSchemaRequest,
  GetDynamicAssessmentResponsesRequest,
  SaveDynamicAssessmentResponsesRequest,
} from '../types/dynamicAssessment';

export type {
  AssessmentFormField,
  AssessmentFormOption,
  AssessmentFormOptionSet,
  AssessmentFormSchema,
  AssessmentFormSection,
  DynamicAssessmentResponse,
  DynamicAssessmentResponseItem,
  DynamicAssessmentSubject,
  GetAssessmentFormSchemaRequest,
  GetDynamicAssessmentResponsesRequest,
  SaveDynamicAssessmentResponseItem,
  SaveDynamicAssessmentResponsesRequest,
} from '../types/dynamicAssessment';

/**
 * Resolves and returns the published form schema for a subject.
 */
export const getAssessmentFormSchema = async (
  request: GetAssessmentFormSchemaRequest
): Promise<AssessmentFormSchema> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<AssessmentFormSchema>>(
      '/DynamicAssessment/form-schema',
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Returns saved dynamic assessment responses for the current subject instance.
 */
export const getDynamicAssessmentResponses = async (
  request: GetDynamicAssessmentResponsesRequest
): Promise<DynamicAssessmentResponse> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<DynamicAssessmentResponse>>(
      '/DynamicAssessment/responses',
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Saves draft or submitted dynamic assessment responses.
 */
export const saveDynamicAssessmentResponses = async (
  request: SaveDynamicAssessmentResponsesRequest
): Promise<DynamicAssessmentResponse> => {
  try {
    const { data } = await axiosInstance.post<ApiEnvelope<DynamicAssessmentResponse>>(
      '/DynamicAssessment/save-responses',
      request
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

/**
 * Loads form schema and saved responses together for a subject.
 */
export const loadDynamicAssessment = async (
  request: GetAssessmentFormSchemaRequest
): Promise<{ schema: AssessmentFormSchema; responses: DynamicAssessmentResponse }> => {
  const [schema, responses] = await Promise.all([
    getAssessmentFormSchema(request),
    getDynamicAssessmentResponses(request),
  ]);

  return { schema, responses };
};
